import React, { useState, useEffect } from "react";
import Breadcrumbs from "../components/BreadCrumbs";
import SearchBar from "../components/searchBar/SearchBar";
import Card from "../components/cards/Card";
import Table from "../components/tables/Table";
import ShowModal from "../utils/ShowModal";
import SupportTicketDetails from "./SupportTicketDetails";
import CustomLoading from "../components/customLoading/CustomLoading";
import { FiBarChart2, FiClock, FiCheckCircle, FiAlertTriangle, } from "react-icons/fi";
import { formatDate, formatDateTime } from "../utils/FormatDateTime";
import { BiRefresh } from "react-icons/bi";
import { supportTicketPatch, getSupportTicketsDashboard } from "../services/supportTickets";
import { toast } from "react-toastify";
import Pagination from "../components/tables/pagination/Pagination";
import Dropdown from "../components/inputFields/DropDown";
import { searchStaffUsers } from "../services/search";
import Button from "../components/buttons/Button";

const CATEGORY_LABELS = {
    app_issue: "App Issue",
    order_issue: "Order Issue",
    device_issue: "Device Issue",
    device_installation: "Device Installation",
    payment_issue: "Payment Issue",
    delivery_issue: "Delivery Issue",
    other: "Other",
};

const formatCategoryLabel = (slug) => {
    if (!slug) return "—";
    return CATEGORY_LABELS[slug] || slug.replace(/_/g, " ");
};

const normalizeStatusValue = (status) => String(status || "").trim().toLowerCase().replace(/\s+/g, "_");

/** Matches backend Ticket.status_choices */
const STATUS_OPTIONS = [
    { label: "Pending", value: "pending" },
    { label: "Resolved", value: "resolved" },
    { label: "Closed", value: "closed" },
    { label: "Reopened", value: "reopened" },
    { label: "Open", value: "open" },
];

const PRIORITY_FILTER_OPTIONS = [
    { label: "Low", value: "low" },
    { label: "Medium", value: "medium" },
    { label: "High", value: "high" },
];

const CATEGORY_FILTER_OPTIONS = Object.entries(CATEGORY_LABELS).map(([value, label]) => ({
    label,
    value,
}));

/** Match DRF-style support-dashboard pagination */
const SUPPORT_TICKETS_PAGE_SIZE = 20;

const SupportTickets = () => {
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [data, setData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [staffUsers, setStaffUsers] = useState([]);

    const [activeTab, setActiveTab] = useState(" ");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterPriority, setFilterPriority] = useState("");
    const [filterCategory, setFilterCategory] = useState("");
    const [filterAssignedTo, setFilterAssignedTo] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [showDetails, setShowDetails] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [tableFilter, setTableFilter] = useState({
        user: "",
    });
    const [selectedSort, setSelectedSort] = useState("total_amount");
    const [selectedSortDir, setSelectedSortDir] = useState("desc");

    // Card data for statistics
    const [cardData, setCardData] = useState([]);

    // Tabs configuration
    const [tabsList, setTabsList] = useState([]);

    // Table headers and keys (aligned with support-dashboard API)
    const headers = [
        { label: "Created", key: "createdAt" },
        { label: "Customer", key: "user", needFilter: true },
        { label: "Problem", key: "problem" },
        { label: "Category", key: "category" },
        { label: "Status", key: "status" },
        { label: "Assigned", key: "assignedTo" },
        { label: "Resolution Slot", key: "resolution" },
        { label: "Last note", key: "lastComment" },
    ];

    const buildDashboardQuery = (overrides = {}) => {
        const s = overrides.searchTerm !== undefined ? overrides.searchTerm : searchTerm;
        const tab = overrides.activeTab !== undefined ? overrides.activeTab : activeTab;
        const fs = overrides.filterStatus !== undefined ? overrides.filterStatus : filterStatus;
        const fp = overrides.filterPriority !== undefined ? overrides.filterPriority : filterPriority;
        const fc = overrides.filterCategory !== undefined ? overrides.filterCategory : filterCategory;
        const fa = overrides.filterAssignedTo !== undefined ? overrides.filterAssignedTo : filterAssignedTo;
        const tf = overrides.tableFilter !== undefined ? overrides.tableFilter : tableFilter;

        const params = new URLSearchParams();
        if (s) params.set("search", s);
        const statusForApi = fs !== "" && fs != null ? fs : tab;
        params.set("status", typeof statusForApi === "string" ? statusForApi : " ");
        const pr = fp != null ? String(fp).trim().toLowerCase() : "";
        if (pr) params.set("priority", pr);
        const cat = fc != null ? String(fc).trim() : "";
        if (cat) params.set("category", cat);
        if (fa !== "" && fa != null && fa !== undefined) params.set("assigned_to", String(fa));
        if (tf.user) params.set("user", tf.user);
        const pageNum = overrides.page !== undefined ? overrides.page : currentPage;
        const size =
            overrides.page_size !== undefined ? overrides.page_size : SUPPORT_TICKETS_PAGE_SIZE;
        params.set("page", String(pageNum));
        params.set("page_size", String(size));
        return `?${params.toString()}`;
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        // Debounce fetchDashboardData before calling API
        if (window.supportTicketsSearchTimeout) {
            clearTimeout(window.supportTicketsSearchTimeout);
        }
        window.supportTicketsSearchTimeout = setTimeout(() => {
            setCurrentPage(1);
            fetchDashboardData(buildDashboardQuery({ searchTerm: term, page: 1 }));
        }, 400);
    };

    const handleTabClick = (tabId) => {
        setActiveTab(tabId);
        setCurrentPage(1);
        fetchDashboardData(buildDashboardQuery({ activeTab: tabId, page: 1 }));
    };
    const handleTableFilter = (filterData) => {
        if (filterData.filter === 'none') {
            setSelectedSort("");
            setSelectedSortDir("");
            const cleared = { user: "" };
            setTableFilter(cleared);
            setCurrentPage(1);
            fetchDashboardData(buildDashboardQuery({ tableFilter: cleared, page: 1 }));
        } else {
            setSelectedSort(filterData.key);
            setSelectedSortDir(filterData.filter);
            const nextTf = {
                ...tableFilter,
                [filterData.key]: filterData.filter
            };
            setTableFilter(nextTf);
            setCurrentPage(1);
            fetchDashboardData(buildDashboardQuery({ tableFilter: nextTf, page: 1 }));
        }
    };

    const handleRefresh = () => {
        setSearchTerm("");
        setActiveTab(" ");
        setFilterStatus("");
        setFilterPriority("");
        setFilterCategory("");
        setFilterAssignedTo("");
        setCurrentPage(1);
        setTableFilter({
            user: "",
        });
        fetchDashboardData(
            buildDashboardQuery({
                searchTerm: "",
                activeTab: " ",
                filterStatus: "",
                filterPriority: "",
                filterCategory: "",
                filterAssignedTo: "",
                tableFilter: { user: "" },
                page: 1,
            })
        );
    };

    const handleTicketOpen = (ticket) => {
        setSelectedTicket(ticket);
        setShowDetails(true);
    };

    const handleTicketClose = () => {
        setShowDetails(false);
        setSelectedTicket(null);
    };

    const handleOnTicketOperation = () => {
        setShowDetails(false);
        setSelectedTicket(null);
        fetchDashboardData(buildDashboardQuery());
    };


    const pageSize = SUPPORT_TICKETS_PAGE_SIZE;

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchDashboardData(buildDashboardQuery({ page }));
    };

    const fetchDashboardData = async (query = "") => {
        setLoading(true);
        try {
            let res = await getSupportTicketsDashboard(query);
            if (res.status === 200) {
                const data = res.data;
                setData(data);
                setCardData([
                    {
                        title: "Total Tickets",
                        count: data.stats[0].value,
                        icon: FiBarChart2,
                        textColor: "text-blue-600",
                        bgGradient: "from-blue-50 to-blue-100",
                    },
                    {
                        title: "Pending",
                        count: data.stats[1].value,
                        icon: FiClock,
                        textColor: "text-yellow-600",
                        bgGradient: "from-yellow-50 to-yellow-100",
                    },
                    {
                        title: "Resolved",
                        count: data.stats[2].value,
                        icon: FiCheckCircle,
                        textColor: "text-green-600",
                        bgGradient: "from-green-50 to-green-100",
                    },
                    {
                        title: "High Priority",
                        count: data.stats[3].value,
                        icon: FiAlertTriangle,
                        textColor: "text-red-600",
                        bgGradient: "from-red-50 to-red-100",
                    },
                    {
                        title: "Closed",
                        count: data.stats[4].value,
                        icon: FiCheckCircle,
                        textColor: "text-orange-600",
                        bgGradient: "from-orange-50 to-orange-100",
                    },
                ]);
                setTabsList([
                    { id: " ", name: "All Tickets", count: data.stats[0]?.value },
                    { id: "pending", name: "Pending", count: data.stats[1]?.value },
                    { id: "open", name: "Open" },
                    { id: "resolved", name: "Resolved", count: data.stats[2]?.value },
                    { id: "closed", name: "Closed", count: data.stats[4]?.value },
                    { id: "reopened", name: "Reopened" },
                    ...(data.stats[5] ? [{ id: "new", name: "New", count: data.stats[5].value }] : []),
                ]);
            } else {
                toast.error("Unable to fetch Data");
            }
            setLoading(false);
            setInitialLoading(false);
        } catch (error) {
            console.error("Error fetching Data:", error);
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    const handleStatusChange = async (value, item) => {
        try {
            let res = await supportTicketPatch(item.id, { status: value });
            if (res.status === 200) {
                toast.success("Status updated successfully");
                fetchDashboardData(buildDashboardQuery());
            } else {
                toast.error("Unable to update status");
            }
        } catch (error) {
            toast.error("Unable to update status");
        }
    };

    const handleAssignedToChange = async (value, item) => {
        try {
            let res = await supportTicketPatch(item.id, { assigned_to: value });
            if (res.status === 200) {
                toast.success("Assigned to updated successfully");
                fetchDashboardData(buildDashboardQuery());
            } else {
                toast.error("Unable to update assigned to");
            }
        } catch (error) {
            toast.error("Unable to update assigned to");
        }
    };

    const fetchStaffUsers = async () => {
        let res = await searchStaffUsers();
        if (res.status === 200) {
            setStaffUsers(res.data);
        }
    };

    useEffect(() => {
        fetchDashboardData(buildDashboardQuery({ page: 1 }));
        fetchStaffUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Show initial loading screen
    if (initialLoading) {
        return (
            <div className="p-4 bg-gray-50 min-h-screen">
                <Breadcrumbs items={[{ name: "Home", path: "/dashboard" }, { name: "Support Tickets" }]} />
                <div className="flex justify-center items-center h-96">
                    <div className="text-center">
                        <CustomLoading size={60} />
                        <p className="mt-4 text-gray-600">Loading support tickets...</p>
                    </div>
                </div>
            </div>
        );
    }

    const getStatusColor = (status) => {
        const s = normalizeStatusValue(status);
        switch (s) {
            case "pending":
                return "bg-amber-50/90";
            case "resolved":
                return "bg-emerald-50/90";
            case "closed":
                return "bg-slate-100/90";
            case "ticket_created":
            case "open":
            case "reopened":
                return "bg-sky-50/90";
            default:
                return "bg-white";
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Breadcrumbs items={[{ name: "Home", path: "/dashboard" }, { name: "Support Tickets" }]} />
                <Button
                    type="button"
                    onClick={() => handleTicketOpen(null)}
                    className="h-10 w-full shrink-0 rounded-lg bg-primary px-5 text-sm font-semibold text-white shadow-sm hover:opacity-95 sm:w-auto sm:self-end"
                >
                    Create Ticket
                </Button>
            </div>

            {/* Statistics Cards */}
            <div className="py-3 mb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {cardData.map((card, idx) => (
                        <Card
                            key={idx}
                            count={card.count}
                            title={card.title}
                            icon={card.icon}
                            textColor={card.textColor}
                            bgGradient={card.bgGradient}
                        />
                    ))}
                </div>
            </div>

            {/* Search + filters + actions — single row (scrolls horizontally on narrow screens) */}
            <div className="mb-4 rounded-xl border border-gray-200/90 bg-white px-3 py-2 shadow-sm ring-1 ring-black/[0.04]">
                <div className="flex w-full min-w-0 flex-nowrap items-center gap-2 overflow-x-auto pb-0.5 sm:gap-3">
                    <div className="min-w-[12rem] max-w-md flex-1 shrink-0">
                        <SearchBar
                            placeholder="Search tickets, customers…"
                            onSearch={handleSearch}
                        />
                    </div>
<div className="flex shrink-0 items-center gap-1.5">
                        <span className="shrink-0 whitespace-nowrap text-xs font-medium text-gray-500">
                            Priority
                        </span>
                        <div className="w-[118px] sm:w-[124px]">
                            <Dropdown
                                compact
                                options={[{ label: "All", value: "" }, ...PRIORITY_FILTER_OPTIONS]}
                                value={filterPriority}
                                onChange={(v) => {
                                    const val = v ?? "";
                                    setFilterPriority(val);
                                    setCurrentPage(1);
                                    fetchDashboardData(buildDashboardQuery({ filterPriority: val, page: 1 }));
                                }}
                                placeholder="All"
                                needSearch={false}
                            />
                        </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                        <span className="shrink-0 whitespace-nowrap text-xs font-medium text-gray-500">
                            Category
                        </span>
                        <div className="w-[140px] sm:w-[156px]">
                            <Dropdown
                                compact
                                options={[{ label: "All", value: "" }, ...CATEGORY_FILTER_OPTIONS]}
                                value={filterCategory}
                                onChange={(v) => {
                                    const val = v ?? "";
                                    setFilterCategory(val);
                                    setCurrentPage(1);
                                    fetchDashboardData(buildDashboardQuery({ filterCategory: val, page: 1 }));
                                }}
                                placeholder="All"
                                needSearch={false}
                            />
                        </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                        <span className="shrink-0 whitespace-nowrap text-xs font-medium text-gray-500">
                            Assigned
                        </span>
                        <div className="w-[150px] sm:w-[168px]">
                            <Dropdown
                                compact
                                options={[
                                    { label: "All", value: "" },
                                    ...staffUsers.map((user) => ({
                                        label: user.name,
                                        value: user.id,
                                    })),
                                ]}
                                value={filterAssignedTo}
                                onChange={(v) => {
                                    const val = v ?? "";
                                    setFilterAssignedTo(val);
                                    setCurrentPage(1);
                                    fetchDashboardData(buildDashboardQuery({ filterAssignedTo: val, page: 1 }));
                                }}
                                placeholder="All"
                                needSearch={false}
                            />
                        </div>
                    </div>

                    {(searchTerm !== "" ||
                        filterPriority ||
                        filterCategory ||
                        filterAssignedTo) && (
                        <button
                            type="button"
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-primary transition-colors hover:bg-gray-50"
                            onClick={handleRefresh}
                            aria-label="Reset filters"
                        >
                            <BiRefresh className="text-xl" />
                        </button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="mb-4">
                <div className="flex justify-start border-b border-slate-800 overflow-x-auto">
                    {tabsList.map((tab) => (
                        <button
                            key={tab.id}
                            className={`px-4 py-2 text-sm font-medium whitespace-nowrap focus:outline-none transition-colors ${
                                activeTab === tab.id
                                    ? "border-b-2 border-slate-800 text-slate-800"
                                    : "text-gray-500 hover:text-slate-800"
                            }`}
                            onClick={() => handleTabClick(tab.id)}
                        >
                            {tab.name}
                            {tab.count !== undefined && (
                                <span className={`ml-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    activeTab === tab.id ? "bg-slate-800 text-white" : "bg-gray-100 text-gray-700"
                                }`}>
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table + pagination (aligned with Orders: table card, pagination bar below) */}
            <div className="relative space-y-4">
                <div className="relative min-h-[12rem]">
                    {loading && (
                        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-white/85 backdrop-blur-[1px]">
                            <div className="text-center">
                                <CustomLoading size={40} />
                                <p className="mt-2 text-sm text-gray-600">Updating…</p>
                            </div>
                        </div>
                    )}
                    <Table
                        density="comfortable"
                        headers={headers}
                        tableFilter={tableFilter}
                        onFilterChange={handleTableFilter}
                        currentSort={selectedSort}
                        currentSortDir={selectedSortDir}
                        rows={data.results?.map((item, index) => {
                        const assignedStaffId =
                            item.assigned_to_id ??
                            staffUsers.find((u) => u.name === item.assigned_to)?.id ??
                            null;
                        const rowNum = (currentPage - 1) * pageSize + index + 1;
                        return {
                            id: (
                                <span
                                    onClick={() => handleTicketOpen(item)}
                                    className="cursor-pointer font-medium tabular-nums text-gray-500 hover:text-primary"
                                >
                                    {rowNum}
                                </span>
                            ),
                            createdAt: (
                                <button
                                    type="button"
                                    onClick={() => handleTicketOpen(item)}
                                    className="text-left hover:text-primary"
                                >
                                    <span className="text-sm font-medium text-gray-900 tabular-nums">
                                        {item.created_at ? formatDate(item.created_at) : "—"}
                                    </span>
                                </button>
                            ),
                            user: (
                                <button
                                    type="button"
                                    onClick={() => handleTicketOpen(item)}
                                    className="max-w-[10rem] min-w-[7.5rem] text-left"
                                >
                                    <span className="block truncate text-sm font-semibold text-gray-900 hover:text-primary">
                                        {item.user || item.new_user_name || "—"}
                                    </span>
                                    <span className="mt-0.5 block truncate text-xs tabular-nums text-gray-500">
                                        {item.phone_number || item.new_user_phone_number || "—"}
                                    </span>
                                </button>
                            ),
                            problem: (() => {
                                const raw = (item.subject || "").trim();
                                const text = raw || "—";
                                return (
                                    <button
                                        type="button"
                                        onClick={() => handleTicketOpen(item)}
                                        className="text-left w-full min-w-[16rem] max-w-[26rem] py-0.5"
                                        title={raw ? raw : undefined}
                                    >
                                        <span className="text-sm font-normal text-gray-900 leading-snug line-clamp-2 break-words">
                                            {text}
                                        </span>
                                    </button>
                                );
                            })(),
                            category: (
                                <div className="max-w-[10rem]">
                                    <span
                                        onClick={() => handleTicketOpen(item)}
                                        className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-800 cursor-pointer hover:bg-slate-100"
                                        title={item.category}
                                    >
                                        {formatCategoryLabel(item.category)}
                                    </span>
                                    {item.category === "other" && item.category_other ? (
                                        <p
                                            className="mt-1 text-xs text-gray-600 truncate"
                                            title={item.category_other}
                                        >
                                            {item.category_other}
                                        </p>
                                    ) : null}
                                </div>
                            ),
                            status: (
                                <div className="min-w-[9.5rem]">
                                    <Dropdown
                                        options={STATUS_OPTIONS}
                                        value={normalizeStatusValue(item.status)}
                                        onChange={(value) => handleStatusChange(value, item)}
                                        placeholder="Status"
                                    />
                                </div>
                            ),
                            assignedTo: (
                                <div className="min-w-[9.5rem]">
                                    <Dropdown
                                        options={staffUsers.map((user) => ({
                                            label: user.name,
                                            value: user.id,
                                        }))}
                                        value={assignedStaffId}
                                        onChange={(value) => handleAssignedToChange(value, item)}
                                        placeholder={item.assigned_to || "Unassigned"}
                                    />
                                </div>
                            ),
                            resolution: (
                                <span
                                    className="text-sm text-gray-700 tabular-nums"
                                    title={item.resolution_date_time_given || ""}
                                >
                                    {item.resolution_date_time_given
                                        ? formatDateTime(item.resolution_date_time_given)
                                        : "—"}
                                </span>
                            ),
                            lastComment: (
                                <span
                                    className="text-sm text-gray-600 line-clamp-2 max-w-[12rem]"
                                    title={item.last_comment || ""}
                                >
                                    {item.last_comment?.trim() ? item.last_comment : "—"}
                                </span>
                            ),
                            rowBgColor: getStatusColor(item.status),
                        };
                        })}
                    />
                </div>
                {data?.count > 0 ? (
                    <Pagination
                        currentPage={currentPage}
                        pageSize={pageSize}
                        totalEntries={data?.count}
                        onPageChange={handlePageChange}
                        data={data}
                        setData={setData}
                    />
                ) : null}
            </div>

            {/* Details Modal */}
            <ShowModal
                isOpen={showDetails}
                title={selectedTicket?.id ? "Support ticket" : "Create Ticket"}
                componentToPass={<SupportTicketDetails ticket={selectedTicket} onOperation={handleOnTicketOperation} />}
                isSidebar={true}
                onRequestClose={handleTicketClose}
                modalWidth={"78%"}
            />
        </div>
    );
};

export default SupportTickets; 